import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { getItems } from "../../firebase/itemsAndInventory/getItems";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Alert,
	Typography,
	IconButton,
	Checkbox,
	Button,
	Box,
	Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ItemInfo } from "../../models/item";
import {
	auditedDeleteItem,
	auditedUpdateItem
} from "../../firebase/auditLog/wrappedOperations";
import Barcode from "./Barcode";
import EditIcon from "@mui/icons-material/Edit";
import EditItemDialog from "./EditItemDialog";
import { formatDate } from "../../utils/calculations";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { exportItemsListToExcel } from "../../utils/exportItemsListToExcel";
import { exportItemsListToPdf } from "../../utils/exportItemsListToPdf";
import { getPartnerInvestmentsByDateRange } from "../../firebase/partnerInvestment/getPartnerInvestmentsByDateRange";
import { buildInvestmentCodeMapForItems } from "../../utils/partnerInvestmentFlow";
import {
	getInitialItemEditState,
	getItemDeleteValidationError,
	getItemEditStateForClose,
	getItemEditStateForOpen,
	updateItemsWithEditedItem
} from "../../utils/itemActionsFlow";

interface ItemsListProps {
	lastDays: number; // If provided, fetch items created in the last 'lastDays' days
	selectable?: boolean;
	selectedKeys?: string[];
	onSelectionChange?: (
		selectedKeys: string[],
		selectedItems?: { [key: string]: ItemInfo }
	) => void;
	onOpenRecord?: () => void;
}
const ItemsList: React.FC<ItemsListProps> = ({
	lastDays,
	selectable = false,
	selectedKeys: propSelectedKeys = [],
	onSelectionChange,
	onOpenRecord
}) => {
	const [editState, setEditState] = useState(getInitialItemEditState);

	const [items, setItems] = useState<{ [key: string]: ItemInfo }>({});
	const [investmentCodes, setInvestmentCodes] = useState<
		Record<string, string | null>
	>({});
	const [selectedKeys, setSelectedKeys] = useState<string[]>(
		propSelectedKeys || []
	);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchItems = async () => {
			try {
				const [fetchedItems, investments] = await Promise.all([
					getItems(),
					getPartnerInvestmentsByDateRange(
						"2000-01-01",
						new Date().toLocaleDateString("en-CA")
					)
				]);
				// Sort items by createdAt descending
				const sortedItems = Object.fromEntries(
					Object.entries(fetchedItems).sort(
						(a, b) =>
							new Date(b[1].createdAt).getTime() -
							new Date(a[1].createdAt).getTime()
					)
				);

				// calculate currentDate - lastDays
				const currentDate = new Date().toLocaleDateString("en-CA");
				const lastDayDate = new Date(
					new Date(currentDate).setDate(
						new Date(currentDate).getDate() - lastDays
					)
				).toLocaleDateString("en-CA");

				// Filter items created in last 2 days and not soft-deleted
				const recentItems: { [key: string]: ItemInfo } = {};
				Object.entries(sortedItems).forEach(
					([key, item]: [string, ItemInfo]) => {
						if (
							!item.isDeleted &&
							item.createdAt &&
							new Date(item.createdAt) >= new Date(lastDayDate)
						) {
							recentItems[key] = item;
						}
					}
				);
				const codeMap = buildInvestmentCodeMapForItems(
					recentItems,
					investments
				);
				setItems(recentItems);
				setInvestmentCodes(codeMap);
			} catch (err) {
				setError("Failed to fetch items. Please try again later.");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		void fetchItems();
	}, [lastDays]);

	useEffect(() => {
		setSelectedKeys(propSelectedKeys || []);
	}, [propSelectedKeys]);

	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	const handleDelete = async (itemId: string) => {
		const item = items[itemId];
		const validationError = getItemDeleteValidationError(item);
		if (validationError) {
			if (validationError === "Item not found.") {
				return;
			}
			alert(validationError);
			return;
		}

		if (window.confirm("Are you sure you want to delete this item?")) {
			try {
				await auditedDeleteItem(itemId);
				const newItems = { ...items };
				delete newItems[itemId];
				setItems(newItems);
			} catch (err) {
				setError("Failed to delete item. Please try again later.");
				console.error(err);
			}
		}
	};

	const handleEdit = (itemId: string, item: ItemInfo) => {
		setEditState(getItemEditStateForOpen(itemId, item));
	};

	const handleEditDialogClose = () => {
		setEditState(getItemEditStateForClose());
	};

	const handleEditSave = async (updatedItem: ItemInfo) => {
		if (!editState.editItemId) return;
		try {
			await auditedUpdateItem(editState.editItemId, updatedItem);
			setItems((prev) =>
				updateItemsWithEditedItem(
					prev,
					editState.editItemId!,
					updatedItem
				)
			);
			handleEditDialogClose();
		} catch (err) {
			setError("Failed to update item. Please try again later.");
			console.error(err);
		}
	};

	const handleExportItems = async () => {
		try {
			await exportItemsListToExcel(items);
		} catch (err) {
			setError("Failed to export items. Please try again later.");
			console.error(err);
		}
	};

	const handleExportItemsPdf = () => {
		try {
			exportItemsListToPdf(items);
		} catch (err) {
			setError("Failed to export items PDF. Please try again later.");
			console.error(err);
		}
	};

	const handlePrintBarcode = (item: ItemInfo, itemKey: string) => {
		// const barcodeValue = `${item.purchasePrice}_${item.name}_${item.sellPrice}_${item.minProfitPercent}`;
		const printWindow = window.open("", "_blank");
		if (printWindow) {
			const printContent = document.createElement("div");
			printWindow.document.body.appendChild(printContent);

			const root = createRoot(printContent);
			root.render(
				<React.StrictMode>
					<Barcode itemKey={itemKey} itemInfo={item} />
					<IconButton
						aria-label="print barcode"
						onClick={() => printWindow.print()}
					>
						Print
					</IconButton>
				</React.StrictMode>
			);
		}
	};

	const handleToggleSelect = (key: string) => {
		let next: string[] = [];
		if (selectedKeys.includes(key)) {
			next = selectedKeys.filter((k) => k !== key);
		} else {
			next = [...selectedKeys, key];
		}
		setSelectedKeys(next);
		if (onSelectionChange) {
			const selectedItems: { [key: string]: ItemInfo } = {};
			next.forEach((k) => {
				if (items[k]) selectedItems[k] = items[k];
			});
			onSelectionChange(next, selectedItems);
		}
	};

	if (loading) {
		return (
			<Paper
				elevation={3}
				sx={{
					p: 2,
					display: "flex",
					justifyContent: "center",
					alignItems: "center"
				}}
			>
				<CircularProgress />
			</Paper>
		);
	}

	if (error) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<>
			{Object.keys(items).length > 0 && (
				<>
					<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
						<Typography variant="h5" gutterBottom>
							Items List
						</Typography>
						<Box
							sx={{
								display: "flex",
								gap: 1,
								alignItems: "center",
								marginBottom: 1
							}}
						>
							<Box>
								<IconButton
									color="primary"
									aria-label="export items list to excel"
									onClick={handleExportItems}
								>
									<DownloadIcon />
								</IconButton>
								<IconButton
									color="secondary"
									aria-label="export items list to pdf"
									onClick={handleExportItemsPdf}
								>
									<PictureAsPdfIcon />
								</IconButton>
							</Box>
							{selectable && selectedKeys.length > 0 ? (
								<Button
									variant="outlined"
									onClick={() =>
										onOpenRecord && onOpenRecord()
									}
								>
									Record Investment for selected items (
									{selectedKeys.length})
								</Button>
							) : null}
						</Box>
						<TableContainer component={Paper}>
							<Table>
								<TableHead>
									<TableRow>
										{selectable ? <TableCell /> : null}
										<TableCell>Item Name</TableCell>
										<TableCell>Investment</TableCell>
										<TableCell>Date</TableCell>
										<TableCell>Purchase Price</TableCell>
										<TableCell>Sell Price</TableCell>
										<TableCell>Minimum Profit %</TableCell>
										<TableCell>Avl. Qty.</TableCell>
										<TableCell>Item Code</TableCell>
										<TableCell>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{Object.entries(items).map(
										([key, item]) => (
											<TableRow key={key}>
												{selectable ? (
													<TableCell>
														<Checkbox
															checked={selectedKeys.includes(
																key
															)}
															onChange={() =>
																handleToggleSelect(
																	key
																)
															}
															disabled={Boolean(
																investmentCodes[
																	key
																]
															)}
														/>
													</TableCell>
												) : null}
												<TableCell>
													{item.name}
												</TableCell>
												<TableCell>
													{investmentCodes[key] ? (
														<Chip
															size="small"
															label={
																investmentCodes[
																	key
																]
															}
															color="warning"
														/>
													) : (
														"-"
													)}
												</TableCell>
												<TableCell>
													{formatDate(item.createdAt)}
												</TableCell>
												<TableCell>{`₹${item.purchasePrice.toFixed(
													2
												)}`}</TableCell>
												<TableCell>{`₹${item.sellPrice.toFixed(
													2
												)}`}</TableCell>
												<TableCell>
													{item.minProfitPercent.toFixed(
														2
													)}
												</TableCell>
												<TableCell>
													{item.availableQuantity?.toFixed(
														2
													)}
												</TableCell>
												<TableCell>
													{item.itemCode}
													<IconButton
														aria-label="copy item code"
														onClick={() =>
															handleCopy(
																item.itemCode
															)
														}
														size="small"
													>
														<ContentCopyIcon fontSize="small" />
													</IconButton>
												</TableCell>
												<TableCell>
													<IconButton
														aria-label="edit"
														onClick={() =>
															handleEdit(
																key,
																item
															)
														}
													>
														<EditIcon />
													</IconButton>
													<IconButton
														aria-label="delete"
														onClick={() =>
															handleDelete(key)
														}
													>
														<DeleteIcon />
													</IconButton>
													<IconButton
														aria-label="print barcode"
														onClick={() =>
															handlePrintBarcode(
																item,
																key
															)
														}
													>
														<PrintIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										)
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
					<EditItemDialog
						open={editState.editDialogOpen}
						item={editState.editItemData}
						onClose={handleEditDialogClose}
						onSave={handleEditSave}
					/>{" "}
				</>
			)}
		</>
	);
};

export default ItemsList;
